import { Group, Stack } from '@mantine/core';
import { FilterStep } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import StyledNumberInput from '@/components/StyledInputFields/StyledNumberInput.tsx';
import {
  DataInput,
  FilterMode,
  FilterType,
  InputSource,
  SpreadsheetFilterMode,
} from '@/types/automationFields.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import InputSourceSelectField from '@/pages/Editor/components/SharedFields/InputSourceSelectField.tsx';
import MultiDataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelectorUI.tsx';
import {
  getMultiLogicDataInputs,
  inputSourceAcceptsDataInput,
  inputSourceAcceptsUserSpecified,
} from '@/pages/Editor/components/SharedFields/utils.ts';
import { STEP_LOGIC_MAX_LENGTH } from '@/utils/constants.ts';

type props = {
  step: FilterStep;
};

type Fields = {
  filter_condition_source: InputSource;
  filter_condition_data_inputs: DataInput[];
  dataset_description: string;
  is_relevant_condition: string;
  is_not_relevant_condition: string;
  filter_mode: FilterMode;
  filter_type: FilterType;
  min_selected_count: number | null;
  max_selected_count: number | null;
  spreadsheet_filter_mode: SpreadsheetFilterMode;
  selection_source: InputSource;
  selection_data_inputs: DataInput[];
  selection_instructions: string;
};

const SELECTION_PLACEHOLDER =
  'Pages 1, 3-5, 8; workbook sheets use {"sheet_names":["Summary"]}, {"source_names":["jun26"]}, or {"display_names":["Workbook - June Summary"]}; directory files use {"display_names":["Building","Fire - Lifeguards"]}, {"source_names":["Building.pdf"]}, {"filenames":["Building.pdf"]}, {"relative_paths":["Nested/Fire.pdf"]}, or {"file_ids":["..."]}';

const SELECTION_DESCRIPTION =
  'Selection mode is intended for exact membership selection. For workbook sheets, use exact JSON selectors like sheet_names, source_names, or display_names when deterministic matching matters. For directory files, use display_names for human-facing names; use source_names, filenames, file_ids, or relative_paths when those values are available.';

const FilterFields = ({ step }: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });

  const emptyDescription = useStepSpecificError(step, StepErrorType.EmptyDescription);
  const invalidLogicDataInput = useStepSpecificError(step, StepErrorType.InvalidLogicDataInput);
  const invalidFilterSelectionCount = useStepSpecificError(
    step,
    StepErrorType.InvalidFilterSelectionCount,
  );
  const filterMode = fields.filter_mode ?? FilterMode.Condition;
  const selectionSource = fields.selection_source ?? InputSource.UserSpecified;
  const selectionInstructions = fields.selection_instructions ?? '';
  const filterConditionDataInputs = getMultiLogicDataInputs(fields.filter_condition_data_inputs);
  const selectionDataInputs = getMultiLogicDataInputs(fields.selection_data_inputs);

  return (
    <Stack w={'100%'}>
      <StyledSelect
        label="Mode"
        placeholder="Select mode"
        data={[
          { value: FilterMode.Condition, label: 'Filter by condition' },
          { value: FilterMode.Selection, label: 'Select exact items' },
        ]}
        value={filterMode}
        onChange={(value) => onChange({ filter_mode: value as FilterMode })}
        required={true}
        allowDeselect={false}
        clearable={false}
      />

      <Group grow align="flex-start">
        <StyledNumberInput
          label="Min selected"
          description="Optional"
          placeholder="Any"
          value={fields.min_selected_count ?? ''}
          min={0}
          allowDecimal={false}
          error={invalidFilterSelectionCount}
          onChange={(newValue) => {
            onChange({ min_selected_count: typeof newValue === 'number' ? newValue : null });
          }}
        />
        <StyledNumberInput
          label="Max selected"
          description="Optional"
          placeholder="Any"
          value={fields.max_selected_count ?? ''}
          min={0}
          allowDecimal={false}
          error={invalidFilterSelectionCount}
          onChange={(newValue) => {
            onChange({ max_selected_count: typeof newValue === 'number' ? newValue : null });
          }}
        />
      </Group>

      {filterMode === FilterMode.Condition && (
        <>
          <StyledSelect
            label="Filter Type"
            placeholder="Select Filter Type"
            data={[
              { value: FilterType.Files, label: 'Filter out files' },
              { value: FilterType.FileContents, label: 'Filter out file content' },
            ]}
            value={fields.filter_type}
            onChange={(value) => onChange({ filter_type: value as FilterType })}
            required={true}
            allowDeselect={false}
            clearable={false}
          />

          {fields.filter_type === FilterType.Files && (
            <StyledSelect
              label="How to handle Excel files"
              data={[
                {
                  value: SpreadsheetFilterMode.TreatAsWorkbook,
                  label: 'Treat as workbook (1 file)',
                },
                {
                  value: SpreadsheetFilterMode.TreatAsSeparateSheets,
                  label: 'Treat as separate sheets (1 file per sheet)',
                },
              ]}
              value={fields.spreadsheet_filter_mode}
              onChange={(value) =>
                onChange({ spreadsheet_filter_mode: value as SpreadsheetFilterMode })
              }
              required={true}
              allowDeselect={false}
              clearable={false}
            />
          )}

          <InputSourceSelectField
            label="Filter Logic Source"
            value={step.filter_condition_source}
            onChange={(value) => onChangeNoDelay({ filter_condition_source: value })}
          />
          {inputSourceAcceptsUserSpecified(fields.filter_condition_source) && (
            <>
              <StyledTextArea
                label="Description of the dataset being filtered"
                value={fields.dataset_description}
                placeholder="E.g., a bank statement with 3 different sections: transactions, personal info, ads"
                onChange={(event) => {
                  onChange({ dataset_description: event.currentTarget.value });
                }}
                required={false}
                autosize={true}
                minRows={2}
                maxLength={STEP_LOGIC_MAX_LENGTH}
              />
              <StyledTextArea
                label="Description of how to determine if file/content is relevant"
                value={fields.is_relevant_condition}
                placeholder="Include if..."
                onChange={(event) => {
                  onChange({ is_relevant_condition: event.currentTarget.value });
                }}
                required={false}
                autosize={true}
                minRows={2}
                error={emptyDescription}
                maxLength={STEP_LOGIC_MAX_LENGTH}
              />
              <StyledTextArea
                label="Description of how to determine if file/content is not relevant"
                value={fields.is_not_relevant_condition}
                placeholder="Do not include if..."
                onChange={(event) => {
                  onChange({ is_not_relevant_condition: event.currentTarget.value });
                }}
                required={false}
                autosize={true}
                minRows={2}
                error={emptyDescription}
                maxLength={STEP_LOGIC_MAX_LENGTH}
              />
            </>
          )}
          {inputSourceAcceptsDataInput(fields.filter_condition_source) && (
            <MultiDataInputSelectorUI
              stepId={step.step_id}
              label={'Filter Logic Data Inputs'}
              error={invalidLogicDataInput}
              dataInputs={filterConditionDataInputs}
              updateDataInputs={(dataInputs) =>
                onChangeNoDelay({
                  filter_condition_data_inputs: dataInputs,
                })
              }
            />
          )}
        </>
      )}

      {filterMode === FilterMode.Selection && (
        <>
          <InputSourceSelectField
            label="Selection Source"
            value={selectionSource}
            onChange={(value) => onChangeNoDelay({ selection_source: value })}
          />

          {inputSourceAcceptsUserSpecified(selectionSource) && (
            <StyledTextArea
              label="Selection"
              description={SELECTION_DESCRIPTION}
              value={selectionInstructions}
              placeholder={SELECTION_PLACEHOLDER}
              onChange={(event) => {
                onChange({ selection_instructions: event.currentTarget.value });
              }}
              required={true}
              autosize={true}
              minRows={3}
              error={emptyDescription}
              maxLength={STEP_LOGIC_MAX_LENGTH}
            />
          )}

          {inputSourceAcceptsDataInput(selectionSource) && (
            <MultiDataInputSelectorUI
              stepId={step.step_id}
              label={'Selection Data Inputs'}
              error={invalidLogicDataInput}
              dataInputs={selectionDataInputs}
              updateDataInputs={(dataInputs) =>
                onChangeNoDelay({
                  selection_data_inputs: dataInputs,
                })
              }
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default FilterFields;
